import { isNumeric } from "@sembark-travel/number-utils"
import { TBookingOptionType, TVisaBooking } from "./store"
import {
	formatMoneyByDecimal,
	moneyParseByDecimal,
	TMoney,
} from "@sembark-travel/money"
import { TextOwnProps } from "@sembark-travel/ui-base"

/**
 *
 * | Function Name                 | Return Type                                                     | Description                                                                     |
 * | ----------------------------- | --------------------------------------------------------------- | ------------------------------------------------------------------------------- |
 * | `isBookingPaid`               | `boolean`                                                       | Checks whether the booking payment or refund is fully completed.                |
 * | `getBookingPaidAmount`        | `{ amount: TMoney, color } | null`                             | Returns the paid amount along with its display color.                           |
 * | `getBookingDueAmount`         | `{ amount: TMoney } | null`                                    | Calculates the remaining payable amount.                                        |
 * | `isBookingPaymentSettled`     | `boolean`                                                       | Checks whether the paid amount covers the booked price with no outstanding due. |
 * | `getBookingRefundAmount`      | `{ paid_refund_amount: TMoney, refund_amount: TMoney } | null` | Returns the refunded and refundable amounts.                                    |
 * | `getBookingNetPayableAmount`  | `{ amount: TMoney } | null`                                    | Calculates the net payable amount based on cancellation or dropped states.      |
 *
 */

export function isBookingPaid(booking: TVisaBooking): boolean {
	const {
		booked_price,
		refunding_payments_amount,
		paid_refunding_payments_amount,
		paid_payments_amount,
		payments_amount,
	} = booking

	if (!booked_price || !isNumeric(booked_price.amount)) return false

	const isRefundMode = refunding_payments_amount !== null

	if (isRefundMode) {
		// If in refund mode, show Green Check when
		// - Refund is complete
		return paid_refunding_payments_amount === refunding_payments_amount
	}
	// If not in refund mode, show Green Check when
	// - Booking is in Booked/Changed stage
	// - or, when booking with no paid instalments is dropped without cancellation charges
	// AND, when all payments are paid.
	// In case payments_amount is null, paid_payments_amount must also be null.
	return (
		// COMPLETED_BOOKING_STATES.includes(booking.latest_booking_stage.state) &&
		paid_payments_amount !== null &&
		payments_amount !== null &&
		paid_payments_amount === payments_amount
	)
}

export function getBookingPaidAmount(booking: TVisaBooking): {
	amount: TMoney
	color: TextOwnProps["color"]
} | null {
	const { paid_payments_amount, payments_amount, currency } = booking
	if (isNumeric(paid_payments_amount)) {
		return {
			amount: moneyParseByDecimal(paid_payments_amount, currency),
			color: "success",
		}
	}

	const hasPositivePaymentsAmount =
		isNumeric(payments_amount) && Number(payments_amount || 0) > 0

	if (hasPositivePaymentsAmount) {
		return {
			amount: moneyParseByDecimal(0, currency),
			color: "muted",
		}
	}

	return null // nothing to show
}

export function getBookingDueAmount(
	booking: TVisaBooking
): { amount: TMoney } | null {
	const { payments_amount, paid_payments_amount, currency } = booking
	if (!isNumeric(paid_payments_amount) || !isNumeric(payments_amount || 0))
		return null
	const due = Number(payments_amount || 0) - Number(paid_payments_amount || 0)
	if (due <= 0) return null

	return { amount: moneyParseByDecimal(due, currency) }
}

export function isBookingPaymentSettled(booking: TVisaBooking): boolean {
	const { booked_price } = booking
	const paid = getBookingPaidAmount(booking)
	const due = getBookingDueAmount(booking)
	if (due) return false
	const paidAmount = paid ? Number(formatMoneyByDecimal(paid.amount)) : 0
	const bookedAmount = Number(formatMoneyByDecimal(booked_price))
	if (bookedAmount > 0) {
		return paidAmount >= bookedAmount
	}
	return false
}

export function getBookingRefundAmount(booking: TVisaBooking): {
	paid_refund_amount: TMoney
	refund_amount: TMoney
} | null {
	const {
		refunding_payments_amount,
		paid_refunding_payments_amount,
		currency,
	} = booking
	if (!isNumeric(refunding_payments_amount)) return null

	return {
		paid_refund_amount: moneyParseByDecimal(
			Number(paid_refunding_payments_amount || 0),
			currency
		),
		refund_amount: moneyParseByDecimal(
			Number(refunding_payments_amount || 0),
			currency
		),
	}
}

export function getBookingNetPayableAmount(
	booking: TVisaBooking
): { amount: TMoney } | null {
	const {
		payments_amount,
		currency,
		latest_booking_stage,
		refunding_payments_amount,
		cancellation_charges,
		booked_price,
	} = booking
	const formattedBookedPrice = Number(formatMoneyByDecimal(booked_price))
	if (isNumeric(payments_amount)) {
		if (
			isNumeric(refunding_payments_amount) &&
			isNumeric(cancellation_charges)
		) {
			return { amount: moneyParseByDecimal(cancellation_charges, currency) }
		}

		if (
			isNumeric(formattedBookedPrice) &&
			Number(payments_amount || 0) === Number(formattedBookedPrice || 0)
		) {
			return { amount: moneyParseByDecimal(payments_amount, currency) }
		}
	}

	if (!isNumeric(payments_amount) && latest_booking_stage.state === "dropped") {
		return { amount: moneyParseByDecimal(0, currency) }
	}

	return null
}

export const REJECTED_OPTIONS = [
	"Failure to meet specific country requirements",
	"Inconsistencies in application or interview",
	"Unclear purpose of travel",
	"Criminal record (if applicable)",
	"Others",
]

export type TRejectedReason =
	| "Failure to meet specific country requirements"
	| "Inconsistencies in application or interview"
	| "Unclear purpose of travel"
	| "Criminal record (if applicable)"
	| "Others"

export const DROPPED_OPTIONS = [
	"Incomplete documentation",
	"Passport expired / invalid",
	"Application withdrawn by applicant",
	"Eligibility criteria not met",
	"Other",
]

export const ALL_STAGES: Array<TBookingOptionType> = [
	{ id: "document_verified", name: "Document Verified" },
	{ id: "appointment_scheduled", name: "Appointment Schedule" },
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const DOCUMENT_PENDING_STAGES: Array<TBookingOptionType> = [
	{ id: "document_verified", name: "Document Verified" },
	{ id: "appointment_scheduled", name: "Appointment Schedule" },
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const DOCUMENT_RECEIVED_STAGES: Array<TBookingOptionType> = [
	{ id: "document_verified", name: "Document Verified" },
	{ id: "appointment_scheduled", name: "Appointment Schedule" },
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const DOCUMENT_VERIFIED_STAGES: Array<TBookingOptionType> = [
	{ id: "document_verified", name: "Document Verified" },
	{ id: "appointment_scheduled", name: "Appointment Schedule" },
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const APPOINTMENT_SCHEDULE_STAGES: Array<TBookingOptionType> = [
	{ id: "appointment_scheduled", name: "Appointment Schedule" },
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const APPOINTMENT_DONE_STAGES: Array<TBookingOptionType> = [
	{ id: "appointment_done", name: "Appointment Done" },
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const IN_PROGRESS_STAGES: Array<TBookingOptionType> = [
	{ id: "in_progress", name: "In Progress" },
	{ id: "approved", name: "Approved" },
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

const APPROVED_STAGES: Array<TBookingOptionType> = [
	{ id: "approved", name: "Approved" },
	{ id: "dropped", name: "Dropped" },
]

const REJECTED_STAGES: Array<TBookingOptionType> = [
	{ id: "rejected", name: "Rejected" },
	{ id: "dropped", name: "Dropped" },
]

export function getAvailableStageOptions(
	latestBookingStage?: TVisaBooking["latest_booking_stage"]
): Array<TBookingOptionType> {
	const stage = latestBookingStage?.state || null
	if (stage === "document_pending") {
		return DOCUMENT_PENDING_STAGES
	}
	if (stage === "document_received") {
		return DOCUMENT_RECEIVED_STAGES
	}
	if (stage === "document_verified") {
		return DOCUMENT_VERIFIED_STAGES
	}
	if (stage === "appointment_scheduled") {
		return APPOINTMENT_SCHEDULE_STAGES
	}
	if (stage === "appointment_done") {
		return APPOINTMENT_DONE_STAGES
	}
	if (stage === "in_progress") {
		return IN_PROGRESS_STAGES
	}
	if (stage === "approved") {
		return APPROVED_STAGES
	}
	if (stage === "rejected") {
		return REJECTED_STAGES
	}
	// fallback, return the broadest options
	return DOCUMENT_PENDING_STAGES
}
